import * as z from "zod/mini";

/**
 * Version of this contract (the interfaces in rules.ts and client.ts). Bump on any breaking change.
 * A host accepts a game only if the game's `contractVersion` is one it supports.
 */
export const GAME_CONTRACT_VERSION = 1;

/** Contract versions this build of the platform can host. */
export const SUPPORTED_CONTRACT_VERSIONS: readonly number[] = [GAME_CONTRACT_VERSION];

export function isContractCompatible(
  contractVersion: number,
  supported: readonly number[] = SUPPORTED_CONTRACT_VERSIONS,
): boolean {
  return supported.includes(contractVersion);
}

export type Device = "desktop" | "phone" | "tablet";
export type InputMethod = "keyboard" | "touch" | "gamepad";
export type ConnectionMode = "online" | "local";

/**
 * Something worth doing in a game (ADR 0043, P5-LOOP-03), earned once and kept: a stat added up
 * over every play ("sum", e.g. 100 rows cleared), missions won ("won"), or stars across missions
 * ("stars", for games that give them). Counted the same in solo and in rooms.
 */
export interface Achievement {
  /** Unique in the game; kebab case. */
  id: string;
  /** Its name, at most 30 characters ("Row gardener"). */
  name: string;
  /** What to do, at most 80 characters ("Clear 100 rows"). */
  about: string;
  kind: "sum" | "won" | "stars";
  /** For "sum": one of the game's outcomeStats. */
  stat?: string;
  /** How many: the stat's total, missions won, or stars. */
  at: number;
}

/** One step of a game's first play (P5-UX-05). */
export interface TeachStep {
  /** What to do, with a keyboard or a mouse: a short line, at most 80 characters. */
  say: string;
  /** The same on a touch screen, where it's different ("Drag to aim" for "Move the mouse to aim"). */
  sayTouch?: string;
  /**
   * What moves it on: moving, one of the buttons, anything the game takes as input (a tap, a
   * shot, an answer), or a few seconds to read it.
   */
  until: "move" | "primary" | "secondary" | "ability" | "input" | "time";
}

export interface Mission {
  id: string;
  name: string;
  /** The self-teaching intro mission (brief §4.5). */
  tutorial?: boolean;
  /**
   * The paid pack this mission belongs to (P4-02, ADR 0013), e.g. `tank-missions-1`; free if absent.
   * A room plays it only if its host owns the pack. Pack missions come after the free ones.
   */
  pack?: string;
  /**
   * A free mission outside the campaign line (ADR 0019), e.g. a run of several missions: it opens
   * once the room has won the named line mission, and winning it opens nothing. It must name a
   * free mission in the line, and can't itself be in a pack.
   */
  opensAfter?: string;
  /**
   * A free mission outside the campaign line that's open from the start (ADR 0019 update,
   * 2026-10-15), e.g. a battle mode next to the co-op line. Winning it opens nothing. Not with
   * `opensAfter` or `pack`.
   */
  openFromStart?: boolean;
  /**
   * A track (ADR 0027): its own line within the game, named for players, e.g. a quiz category.
   * Missions with the same track form a line in manifest order: the first is open from the start,
   * each next opens when the one before it is won. Not with `pack`, `opensAfter` or `openFromStart`.
   */
  track?: string;
  /**
   * A match against each other rather than together (ADR 0028): everyone for themselves, and
   * with `teams`, also two against two (four players). The outcome's `stats.winner` names the
   * winning slot + 1, or with teams `stats.winnerTeam` the winning team (1 or 2); 0 is a draw.
   */
  battle?: boolean;
  teams?: boolean;
}

/** A choice each player makes before a match (e.g. a role), passed to the rules as `PlayerSlot.loadout`. */
export interface Loadout {
  /** Stable kebab-case id: what the rules receive. */
  id: string;
  name: string;
  /** One line on what it does, shown where players choose. */
  summary: string;
}

/** The platform's button bits (apps/web/src/input/controls.ts) a game can give a meaning and a label. */
export type ControlButton = "primary" | "secondary" | "ability";

/**
 * A game's controls (ADR 0017): which buttons it uses and what they're called. The platform draws
 * exactly these on touch screens and names them in the keyboard hint; moving is always there. A
 * game without buttons omits `controls`. **List them from the thumb's edge inward**: the first sits
 * at the screen's edge under the tapping thumb, the last nearest the middle, mirrored for
 * left-handed players (their choice on the You page), with the stick or swipe alike.
 */
export interface ManifestControls {
  buttons: { button: ControlButton; label: string }[];
  /**
   * How touch screens steer it (ADR 0023). `stick` (the default): a floating thumb stick, for
   * games you steer continuously. `swipe`: gestures on the game itself, for games that move in
   * steps: a drag left or right moves one step per finger-width, dragging down and holding pushes
   * down, a tap presses `primary` and a quick flick down presses `secondary`; the buttons sit in a
   * bar under the game instead of over it, and the game is played upright. `tap` (ADR 0024): the
   * game reads taps on its own canvas (pointer events on its mount), for games played by touching
   * what's on screen; the buttons sit in the same bar.
   */
  touch?: "stick" | "swipe" | "tap";
}

/** What the platform needs to know about a game before loading it. */
export interface GameManifest {
  /** Stable kebab-case id, e.g. "beanguard". Never reused. */
  id: string;
  name: string;
  /** Semver of the game bundle. Pinned per match (ADR 0004). */
  version: string;
  /** GAME_CONTRACT_VERSION this game implements. */
  contractVersion: number;
  /** Current binary snapshot layout. decodeSnapshot must also read snapshotVersion - 1 and + 1 (ADR 0004/0011). */
  snapshotVersion: number;
  players: { min: number; max: number };
  devices: Device[];
  inputs: InputMethod[];
  modes: ConnectionMode[];
  /** Fixed simulation rate in Hz (ADR 0004: 20). */
  tickHz: number;
  missions: Mission[];
  /** What each player chooses between before a match (P2-WEB-03). Omit for games without a choice. */
  loadouts?: Loadout[];
  /** The buttons it uses, with their names (ADR 0017). Omit for a game that only moves. */
  controls?: ManifestControls;
  /** Asset paths the game needs when playing without a network (offline/local track, ADR 0014). */
  offlineAssets: string[];
  /**
   * The outcome stats worth counting over time (P5-AN-04), at most 4, e.g. `["kills"]`: each is
   * recorded as `stat_<name>` on match_ended and solo_ended. `stars` and `missions` always are.
   */
  outcomeStats?: string[];
  /**
   * The missions that can be the daily (P4-TANK-03, `dailyOf`), e.g. a quiz's easier levels. Omit
   * for every free mission that isn't a tutorial, a battle or a run.
   */
  daily?: string[];
  /**
   * The issues the game can report about itself (P3-OBS-04, `LoadContext.reportIssue`): a code and
   * how bad it is ("error": play broke or will; "warning": play goes on, worse).
   */
  issues?: { code: string; severity: "error" | "warning" }[];
  /**
   * Short tips for after a lost play (ADR 0043, the next step): one is shown with "Try again", in
   * turn. A sentence each, for players, e.g. "Pass a pod to a friend when you're stuck."
   */
  tips?: string[];
  /**
   * A first play that teaches (P5-UX-05): steps the platform shows over the game, one at a time,
   * the first time someone plays it, solo or in a room. Each waits for what it asks, then the next
   * shows; every built-in game has two to four.
   */
  teach?: TeachStep[];
  /**
   * What's worth doing here (ADR 0043, P5-LOOP-03): three to six achievements, shown on the result
   * when earned and kept in "Your Obean".
   */
  achievements?: Achievement[];
  /**
   * Beans for the game's own outcome stats (ADR 0043), on top of the platform's for finishing,
   * winning and stars: `each` per unit of `stat`, at most `max` a play, shown as `label`, e.g.
   * `{ stat: "right", each: 2, max: 30, label: "Right answers" }`. At most three.
   */
  beans?: { stat: string; each: number; max: number; label: string }[];
  /**
   * The daily's score with a story (ADR 0041, `dailyTier`): the outcome stat it's told in, e.g.
   * "14 rows", or with `of` a share of another stat, "9 of 10 differences"; five tier names in the
   * game's own world, lowest first; and the symbol its share line is drawn in, e.g. "🔍".
   */
  dailyScore?: DailyScore;
}

/** How a game tells its daily's score (manifest.dailyScore, ADR 0041). */
export interface DailyScore {
  stat: string;
  /** Out of this stat: the tier is the share of it. Without it, the tier is the result and stars. */
  of?: string;
  /** The stat's unit, one and many: ["row", "rows"]. */
  unit: [string, string];
  mark: string;
  tiers: [string, string, string, string, string];
}

const id = z.string().check(z.regex(/^[a-z][a-z0-9-]{0,39}$/));
const semver = z.string().check(z.regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/));
const positiveInt = z.number().check(z.int(), z.gte(1));

export const gameManifestSchema = z.object({
  id,
  name: z.string().check(z.minLength(1), z.maxLength(60)),
  version: semver,
  contractVersion: positiveInt,
  snapshotVersion: positiveInt,
  players: z
    .object({ min: positiveInt, max: z.number().check(z.int(), z.gte(1), z.lte(8)) })
    .check(z.refine((p) => p.min <= p.max, "players.min must be <= players.max")),
  devices: z.array(z.enum(["desktop", "phone", "tablet"])).check(z.minLength(1)),
  inputs: z.array(z.enum(["keyboard", "touch", "gamepad"])).check(z.minLength(1)),
  modes: z.array(z.enum(["online", "local"])).check(z.minLength(1)),
  tickHz: z.number().check(z.int(), z.gte(5), z.lte(60)),
  loadouts: z.optional(
    z
      .array(
        z.object({
          id,
          name: z.string().check(z.minLength(1), z.maxLength(30)),
          summary: z.string().check(z.minLength(1), z.maxLength(80)),
        }),
      )
      .check(
        z.maxLength(8),
        z.refine(
          (ls) => new Set(ls.map((l) => l.id)).size === ls.length,
          "loadout ids must be unique",
        ),
      ),
  ),
  controls: z.optional(
    z.object({
      buttons: z
        .array(
          z.object({
            button: z.enum(["primary", "secondary", "ability"]),
            label: z.string().check(z.minLength(1), z.maxLength(12)),
          }),
        )
        .check(
          z.maxLength(3),
          z.refine(
            (bs) => new Set(bs.map((b) => b.button)).size === bs.length,
            "each control button once",
          ),
        ),
      touch: z.optional(z.enum(["stick", "swipe", "tap"])),
    }),
  ),
  missions: z
    .array(
      z.object({
        id,
        name: z.string().check(z.minLength(1), z.maxLength(60)),
        tutorial: z.optional(z.boolean()),
        pack: z.optional(id),
        opensAfter: z.optional(id),
        openFromStart: z.optional(z.boolean()),
        track: z.optional(z.string().check(z.minLength(1), z.maxLength(30))),
        battle: z.optional(z.boolean()),
        teams: z.optional(z.boolean()),
      }),
    )
    .check(z.minLength(1)),
  offlineAssets: z.array(z.string().check(z.maxLength(200))),
  daily: z.optional(z.array(id).check(z.minLength(1), z.maxLength(200))),
  issues: z.optional(
    z
      .array(
        z.object({
          code: z.string().check(z.regex(/^[a-z][a-z0-9_]{0,39}$/)),
          severity: z.enum(["error", "warning"]),
        }),
      )
      .check(
        z.maxLength(20),
        z.refine(
          (is) => new Set(is.map((i) => i.code)).size === is.length,
          "issue codes once each",
        ),
      ),
  ),
  beans: z.optional(
    z
      .array(
        z.object({
          stat: z.string().check(z.regex(/^[a-z][a-z0-9_]{0,29}$/)),
          each: z.number().check(z.gt(0), z.lte(20)),
          max: z.number().check(z.int(), z.gte(1), z.lte(100)),
          label: z.string().check(z.minLength(1), z.maxLength(30)),
        }),
      )
      .check(z.maxLength(3)),
  ),
  dailyScore: z.optional(
    z.object({
      stat: z.string().check(z.regex(/^[a-z][a-z0-9_]{0,29}$/)),
      of: z.optional(z.string().check(z.regex(/^[a-z][a-z0-9_]{0,29}$/))),
      unit: z.tuple([
        z.string().check(z.minLength(1), z.maxLength(24)),
        z.string().check(z.minLength(1), z.maxLength(24)),
      ]),
      // One symbol, maybe with a variation selector: what the share line is drawn in.
      mark: z.string().check(z.minLength(1), z.maxLength(4)),
      tiers: z.array(z.string().check(z.minLength(1), z.maxLength(24))).check(
        z.length(5),
        z.refine((ts) => new Set(ts).size === ts.length, "tier names once each"),
      ),
    }),
  ),
  tips: z.optional(
    z.array(z.string().check(z.minLength(1), z.maxLength(140))).check(z.maxLength(12)),
  ),
  achievements: z.optional(
    z
      .array(
        z.object({
          id: z.string().check(z.regex(/^[a-z][a-z0-9-]{0,39}$/)),
          name: z.string().check(z.minLength(1), z.maxLength(30)),
          about: z.string().check(z.minLength(1), z.maxLength(80)),
          kind: z.enum(["sum", "won", "stars"]),
          stat: z.optional(z.string().check(z.regex(/^[a-z][a-z0-9_]{0,29}$/))),
          at: z.number().check(z.int(), z.gte(1), z.lte(1_000_000)),
        }),
      )
      .check(
        z.maxLength(6),
        z.refine(
          (as) => new Set(as.map((a) => a.id)).size === as.length,
          "achievement ids once each",
        ),
      ),
  ),
  teach: z.optional(
    z
      .array(
        z.object({
          say: z.string().check(z.minLength(1), z.maxLength(80)),
          sayTouch: z.optional(z.string().check(z.minLength(1), z.maxLength(80))),
          until: z.enum(["move", "primary", "secondary", "ability", "input", "time"]),
        }),
      )
      .check(z.minLength(1), z.maxLength(4)),
  ),
  // Names that fit an event property as `stat_<name>` (docs/metrics.md).
  outcomeStats: z.optional(
    z.array(z.string().check(z.regex(/^[a-z][a-z0-9_]{0,29}$/))).check(
      z.maxLength(4),
      z.refine((ns) => new Set(ns).size === ns.length, "outcome stats once each"),
    ),
  ),
});

export type ManifestCheck = { ok: true; manifest: GameManifest } | { ok: false; errors: string[] };

/** Validate an untrusted manifest (e.g. from a game bundle) and its contract compatibility. */
export function checkManifest(input: unknown): ManifestCheck {
  const parsed = z.safeParse(gameManifestSchema, input);
  if (!parsed.success) {
    return {
      ok: false,
      errors: parsed.error.issues.map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`),
    };
  }
  const manifest = parsed.data as GameManifest;
  // ADR 0019: a mission outside the line opens after a free mission that is in it.
  const line = new Set(
    manifest.missions
      .filter((m) => !m.pack && !m.opensAfter && !m.openFromStart && m.track === undefined)
      .map((m) => m.id),
  );
  const misplaced = manifest.missions.flatMap((m, i) =>
    m.teams && (!m.battle || manifest.players.max < 4)
      ? [`missions.${String(i)}: two against two needs a battle mission and four players`]
      : m.track !== undefined &&
          (m.pack !== undefined || m.opensAfter !== undefined || m.openFromStart)
        ? [`missions.${String(i)}: a mission in a track opens by its track alone`]
        : m.openFromStart && (m.opensAfter !== undefined || m.pack !== undefined)
          ? [`missions.${String(i)}: an open mission can't also open after another or be in a pack`]
          : m.opensAfter === undefined
            ? []
            : m.pack !== undefined
              ? [`missions.${String(i)}: a mission in a pack can't open after another`]
              : line.has(m.opensAfter)
                ? []
                : [
                    `missions.${String(i)}: opensAfter "${m.opensAfter}" isn't a free mission in the line`,
                  ],
  );
  if (misplaced.length > 0) return { ok: false, errors: misplaced };
  // The daily (P4-TANK-03) is open to everyone: free missions of this game, not battles.
  const dailyWrong = (manifest.daily ?? []).flatMap((id) => {
    const m = manifest.missions.find((x) => x.id === id);
    return !m
      ? [`daily: "${id}" isn't a mission of this game`]
      : m.pack !== undefined || m.battle
        ? [`daily: "${id}" must be a free mission that isn't a battle`]
        : [];
  });
  if (dailyWrong.length > 0) return { ok: false, errors: dailyWrong };
  if (!isContractCompatible(manifest.contractVersion)) {
    return {
      ok: false,
      errors: [
        `contractVersion ${manifest.contractVersion} not supported (host supports ${SUPPORTED_CONTRACT_VERSIONS.join(", ")})`,
      ],
    };
  }
  return { ok: true, manifest };
}
